import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Alert } from 'react-bootstrap';
import { BasicInformation } from './Tabs/BasicInformation';
import { ProductImages } from './Tabs/ProductImages';
import { PricingTaxStock } from './Tabs/PricingTaxStock';
import { ProductSpecifications } from './Tabs/ProductSpecifications';
import { ProductPreview } from './Previews/ProductPreview';
import './ProductFormSystem.css';

import api from "../../../src/seller-apis/api";
import servicesApi from "../../../src/seller-apis/servicesApi";

export const ProductFormSystem = ({ match }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // NEW: Refs for scrolling to errors
  const alertRef = useRef(null);
  const formContentRef = useRef(null);

  // NEW: Add states to track available category levels
  const [categoryLevels, setCategoryLevels] = useState({
    hasParentCategories: false,
    hasChildCategories: false,
    hasGrandChildCategories: false
  });

  React.useEffect(() => {
    setValidationErrors({});
    setShowValidationAlert(false);
  }, []);

  const [formData, setFormData] = useState({
    productName: '',
    brand: '',
    brandid: '',
    countryOfOrigin: '',
    categories: [],
    selectedCategoryName: '',
    keywords: [],
    shortDescription: '',
    fullDescription: '',
    
    manufacturerName: '',
    manufacturerAddress: '',
    manufacturerPincode: '',
    
    packerName: '',
    packerAddress: '',
    packerPincode: '',
    sameAsManufacturer: false,
    
    importerName: '',
    importerAddress: '',
    importerPincode: '',
    
    images: [],
    coverImage: '',
    image1: '',
    image2: '',
    image3: '',
    videoFile: '',
    videoUrl: '',
    transferPrice: '',
    gst: '',
    hsnCode: '',
    stockCount: '',
    packages: [],
    // weight: '',
    // weightUnit: '',
    // length: '',
    // width: '',
    // height: '',
    // dimensionUnit: '',
    additionalSpecs: []
  });

  useEffect(() => {
    const fetchProductData = async () => {
      const productId = match?.params?.id;
      
      if (!productId) {
        console.log('No product ID found, skipping fetch');
        return;
      }

      setIsLoading(true);
      
      try {
        const response = await api.product.getProductById(productId);
        const product = response.data;

        const lastCategory = product.categories?.[product.categories.length - 1];
        
        let brandId = '';
        let brandName = '';
        if (product.manufacturer) {
          try {
            const brandData = await api.brand.getManufacturers({
              filter: { manufacturerId: parseInt(product.manufacturer) }
            });
            if (brandData.data?.length > 0) {
              brandId = brandData.data[0].manufacturerId.toString();
              brandName = brandData.data[0].name;
            }
          } catch (err) {
            console.error('Error fetching brand:', err);
          }
        }

        const images = product.images || [];
        const imageUrls = images.map(img => 
          typeof img === 'string' ? img : img.image
        );

        let videoUrl = '';
        let videoFile = '';
        if (product.videos?.length > 0 && product.videos[0]) {
          if (product.videos[0].includes('http')) {
            videoUrl = product.videos[0];
          } else {
            videoFile = product.videos[0];
          }
        }

        const categoryIds = product.categories?.map(cat => cat._id || cat) || [];
        
        const grandCategory = categoryIds[0] || '';
        const parentCategory = categoryIds[1] || '';
        const childCategory = categoryIds[2] || '';
        const grandChildCategory = categoryIds[3] || '';
        
        let packagesArray = [];

        const extractImageSlots = (imagesArray) => {
          const imgs = imagesArray || [];
          return {
            coverImage: imgs[0] || '',
            image1: imgs[1] || '',
            image2: imgs[2] || '',
            image3: imgs[3] || '',
            images: imgs,
            imageUrl: imgs[0] || ''
          };
        };

        const hasSpecPackages = product.specifications?.packages?.length > 0;
        const hasPkgs = product.pkgs?.length > 0;

        if (hasSpecPackages && hasPkgs) {
          
          packagesArray = product.specifications.packages.map((specPkg, index) => {
            const pkgData = product.pkgs[index] || {};
            const pkgImages = pkgData.images || [];
            const hasImageSlots = specPkg.coverImage || specPkg.image1 || specPkg.image2 || specPkg.image3;
            
            let imageData;
            if (hasImageSlots) {
              imageData = {
                coverImage: specPkg.coverImage || '',
                image1: specPkg.image1 || '',
                image2: specPkg.image2 || '',
                image3: specPkg.image3 || '',
                images: specPkg.images || pkgImages,
                imageUrl: specPkg.imageUrl || specPkg.coverImage || ''
              };
            } else {
              imageData = extractImageSlots(pkgImages);
            }
            
            const merged = {
              productName: pkgData.productName || specPkg.productName || '',
              weight: specPkg.weight || String(pkgData.actualWeight || pkgData.weight || ''),
              weightUnit: specPkg.weight ? (specPkg.weightUnit || 'kg') : '',
              length: specPkg.length || String(pkgData.packageLength || ''),
              width: specPkg.width || String(pkgData.packageWidth || ''),
              height: specPkg.height || String(pkgData.packageHeight || ''),
              dimensionUnit: (specPkg.length || specPkg.width || specPkg.height) ? (specPkg.dimensionUnit || 'cm') : '',
              ...imageData
            };
            
            return merged;
          });
        }
        else if (hasSpecPackages) {
          
          packagesArray = product.specifications.packages.map((specPkg, index) => {
            const hasImageSlots = specPkg.coverImage || specPkg.image1 || specPkg.image2 || specPkg.image3;
            
            let imageData;
            if (hasImageSlots) {
              imageData = {
                coverImage: specPkg.coverImage || '',
                image1: specPkg.image1 || '',
                image2: specPkg.image2 || '',
                image3: specPkg.image3 || '',
                images: specPkg.images || [],
                imageUrl: specPkg.imageUrl || specPkg.coverImage || ''
              };
            } else {
              imageData = extractImageSlots(specPkg.images);
            }
            
            return {
              productName: specPkg.productName || '',
              weight: specPkg.weight || '',
              weightUnit: specPkg.weight ? (specPkg.weightUnit || 'kg') : '',
              length: specPkg.length || '',
              width: specPkg.width || '',
              height: specPkg.height || '',
              dimensionUnit: (specPkg.length || specPkg.width || specPkg.height) ? (specPkg.dimensionUnit || 'cm') : '',
              ...imageData
            };
          });
        }
        else if (hasPkgs) { 
          packagesArray = product.pkgs.map((pkg, index) => {
            const imageData = extractImageSlots(pkg.images);
            
            return {
              productName: pkg.productName || '',
              weight: String(pkg.actualWeight || pkg.weight || ''),
              weightUnit: (pkg.actualWeight || pkg.weight) ? (pkg.weightUnit || 'kg') : '',
              length: String(pkg.packageLength || ''),
              width: String(pkg.packageWidth || ''),
              height: String(pkg.packageHeight || ''),
              dimensionUnit: (pkg.packageLength || pkg.packageWidth || pkg.packageHeight) ? (pkg.dimensionUnit || 'cm') : '',
              ...imageData
            };
          });
        }
        else {
          const hasWeight = product.specifications?.weight;
          const hasDimensions = product.specifications?.dimensions?.length || 
                               product.specifications?.dimensions?.width || 
                               product.specifications?.dimensions?.height;
          
          packagesArray = [{
            productName: '',
            weight: product.specifications?.weight || '',
            weightUnit: hasWeight ? (product.specifications?.weightUnit || 'kg') : '',
            length: product.specifications?.dimensions?.length || '',
            width: product.specifications?.dimensions?.width || '',
            height: product.specifications?.dimensions?.height || '',
            dimensionUnit: hasDimensions ? (product.specifications?.dimensions?.unit || 'cm') : '',
            coverImage: '',
            image1: '',
            image2: '',
            image3: '',
            images: [],
            imageUrl: ''
          }];
        }

        const updatedFormData = {
          productName: product.productName || '',
          brand: brandName,
          brandid: brandId,
          countryOfOrigin: product.originCountry || '',
          categories: categoryIds,
          selectedCategoryName: lastCategory?.categoryName || '',
          
          grandCategory: grandCategory,
          parentCategory: parentCategory,
          childCategory: childCategory,
          grandChildCategory: grandChildCategory,
          
          keywords: product.metaInformation?.keywords || [],
          shortDescription: product.shortDescription || '',
          fullDescription: product.description || '',
          
          manufacturerName: product.manufacturerName || '',
          manufacturerAddress: product.manufacturerAddress || '',
          manufacturerPincode: product.manufacturerPincode || '',
          
          packerName: product.packerName || '',
          packerAddress: product.packerAddress || '',
          packerPincode: product.packerPincode || '',
          sameAsManufacturer: product.sameAsManufacturer || false,
          
          importerName: product.importerName || '',
          importerAddress: product.importerAddress || '',
          importerPincode: product.importerPincode || '',
          
          images: imageUrls,
          coverImage: imageUrls[0] || '',
          image1: imageUrls[1] || '',
          image2: imageUrls[2] || '',
          image3: imageUrls[3] || '',
          videoFile: videoFile,
          videoUrl: videoUrl,
          
          transferPrice: product.price?.toString() || '',
          gst: product.taxClass?.toString() || '0',
          hsnCode: product.HSN || '',
          stockCount: product.stock?.toString() || '',
          
          packages: packagesArray,
          // weight: product.specifications?.weight || '',
          // weightUnit: product.specifications?.weight ? (product.specifications?.weightUnit || 'kg') : '',
          // length: product.specifications?.dimensions?.length || '',
          // width: product.specifications?.dimensions?.width || '',
          // height: product.specifications?.dimensions?.height || '',
          // dimensionUnit: (product.specifications?.dimensions?.length || product.specifications?.dimensions?.width || product.specifications?.dimensions?.height) ? (product.specifications?.dimensions?.unit || 'cm') : '',
          additionalSpecs: product.specifications?.additionalSpecs || []
        };

        setFormData(updatedFormData);

      } catch (error) {
        console.error('Error fetching product:', error);
        alert('Failed to load product data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductData();
  }, [match?.params?.id]);

  const tabs = [
    { 
      id: 0, 
      title: 'Basic Information', 
      icon: 'bi-box',
      component: BasicInformation 
    },
    { 
      id: 1, 
      title: 'Product Images', 
      icon: 'bi-image',
      component: ProductImages 
    },
    { 
      id: 2, 
      title: 'Pricing, Tax & Stock', 
      icon: 'bi-currency-dollar',
      component: PricingTaxStock 
    },
    { 
      id: 3, 
      title: 'Packages', 
      icon: 'bi-tag',
      component: ProductSpecifications,
      optional: true // Mark as optional
    }
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    const fieldMappings = {
      transferPrice: 'price',
      gst: 'taxClass', 
      stockCount: 'stock'
    };
    
    const oldFieldName = fieldMappings[field];
    if (oldFieldName && validationErrors[oldFieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[oldFieldName];
        return newErrors;
      });
    }
  };

  // NEW: Enhanced auto-scroll to error
  const scrollToError = () => {
    // First, scroll to the alert
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
    
    // Then, after a short delay, try to scroll to the first error field
    setTimeout(() => {
      const errorFields = [
        'productName', 'countryOfOrigin', 'brandid', 'brand', 'grandCategory', 'parentCategory', 
        'childCategory', 'grandChildCategory', 'keywords', 'shortDescription', 
        'fullDescription', 'coverImage', 'images', 'transferPrice', 'gst', 
        'stockCount', 'packageWeight', 'packageLength', 'packageWidth', 'packageHeight'
      ];
      
      for (const field of errorFields) {
        if (validationErrors[field]) {
          // Try to find the input element by various methods
          let element = document.querySelector(`[name="${field}"]`) ||
                       document.querySelector(`#${field}`) ||
                       document.querySelector(`input[placeholder*="${field}"]`) ||
                       document.querySelector(`textarea[placeholder*="${field}"]`) ||
                       document.querySelector(`select[name="${field}"]`);
          
          // If specific element not found, try to find by error message
          if (!element) {
            const labels = Array.from(document.querySelectorAll('label'));
            const matchingLabel = labels.find(label => 
              label.textContent.toLowerCase().includes(field.toLowerCase().replace(/([A-Z])/g, ' $1').trim())
            );
            if (matchingLabel) {
              element = matchingLabel.closest('.form-group') || matchingLabel.nextElementSibling;
            }
          }
          
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            
            // Focus the element if it's an input
            if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
              setTimeout(() => element.focus(), 300);
            }
            break;
          }
        }
      }
    }, 300);
  };

  const validateTab = (tabId) => {
    const errors = {};
    
    switch (tabId) {
      case 0:
        if (!formData.productName?.trim()) {
          errors.productName = 'Product name is required';
        } else if (formData.productName.length < 3) {
          errors.productName = 'Product name must be at least 3 characters';
        }
        if (!formData.countryOfOrigin) {
          errors.countryOfOrigin = 'Country of origin is required';
        }
        
        // NEW: Brand validation
        if (!formData.brandid) {
          errors.brandid = 'Please select a brand from the dropdown';
        }
        
        if (!formData.grandCategory) {
          errors.grandCategory = 'Please select a grand category';
        } else {
          const categoryLevels = formData._categoryLevels || {};
          
          if (categoryLevels.hasParentCategories && !formData.parentCategory) {
            errors.parentCategory = 'Please select a parent category';
          }
          
          if (categoryLevels.hasChildCategories && !formData.childCategory) {
            errors.childCategory = 'Please select a child category';
          }
          
          if (categoryLevels.hasGrandChildCategories && !formData.grandChildCategory) {
            errors.grandChildCategory = 'Please select a grand child category';
          }
        }
        
        if (!formData.keywords?.length) {
          errors.keywords = 'At least one keyword is required';
        }
        if (!formData.shortDescription?.trim()) {
          errors.shortDescription = 'Short description is required';
        } else if (formData.shortDescription.length > 160) {
          errors.shortDescription = 'Short description must be 160 characters or less';
        }
        if (!formData.fullDescription?.trim()) {
          errors.fullDescription = 'Full description is required';
        } else if (formData.fullDescription.length < 100) {
          errors.fullDescription = 'Full description must be at least 100 characters';
        }
        break;
        
      case 1:
        if (!formData.coverImage) {
          errors.coverImage = 'Cover image is required';
        }
        if (!formData.images || formData.images.length === 0) {
          errors.images = 'At least one product image is required';
        }
        break;
        
      case 2:
        if (!formData.transferPrice) {
          errors.transferPrice = 'Transfer price is required';
        } else if (isNaN(formData.transferPrice) || Number(formData.transferPrice) <= 0) {
          errors.transferPrice = 'Please enter a valid transfer price';
        }
        if (!formData.gst && formData.gst !== '0') {
          errors.gst = 'GST rate is required';
        }
        if (!formData.stockCount && formData.stockCount !== '0') {
          errors.stockCount = 'Stock quantity is required';
        } else if (isNaN(formData.stockCount) || Number(formData.stockCount) < 0) {
          errors.stockCount = 'Please enter a valid stock quantity';
        }
        break;
        
      case 3:
  // Package validation - if ANY field is filled, ALL fields are mandatory
  const packages = formData.packages || [];
  packages.forEach((pkg, index) => {
    // Check if ANY field has data
    const hasAnyField = !!(
      pkg.productName?.trim() ||
      pkg.weight ||
      pkg.length ||
      pkg.width ||
      pkg.height ||
      pkg.coverImage ||
      pkg.image1 ||
      pkg.image2 ||
      pkg.image3
    );
    
    if (hasAnyField) {
      // If any field is filled, ALL fields become mandatory
      
      // Package name is mandatory
      if (!pkg.productName?.trim()) {
        errors[`package ${index+1} productName`] = `Package ${index + 1}: Package name is required when any package details are filled`;
      }
      
      // Weight is mandatory
      if (!pkg.weight) {
        errors[`package ${index+1} weight`] = `Package ${index + 1}: Weight is required when any package details are filled`;
      }
      
      // Length is mandatory
      if (!pkg.length) {
        errors[`package ${index+1} length`] = `Package ${index + 1}: Length is required when any package details are filled`;
      }
      
      // Width is mandatory
      if (!pkg.width) {
        errors[`package ${index+1} width`] = `Package ${index + 1}: Width is required when any package details are filled`;
      }
      
      // Height is mandatory
      if (!pkg.height) {
        errors[`package${index+1} height`] = `Package ${index + 1}: Height is required when any package details are filled`;
      }
      
      // Dimension unit is mandatory
      // if (!pkg.dimensionUnit) {
      //   errors[`package ${index+1} dimensionUnit`] = `Package ${index + 1}: Dimension unit is required when any package details are filled`;
      // }
    }
  });
  break;
    }
    
    return errors;
  };

  const isTabValid = (tabId) => {
    const errors = validateTab(tabId);
    return Object.keys(errors).length === 0;
  };

  const handleTabChange = (tabId) => {
    if (tabId <= activeTab) {
      setActiveTab(tabId);
      setShowValidationAlert(false);
      return;
    }

    let canProceed = true;
    let allErrors = {};
    
    setValidationErrors({});
    
    for (let i = 0; i < tabId; i++) {
      const tabErrors = validateTab(i);
      if (Object.keys(tabErrors).length > 0) {
        allErrors = { ...allErrors, ...tabErrors };
        canProceed = false;
      }
    }
    
    if (canProceed) {
      setActiveTab(tabId);
      setValidationErrors({});
      setShowValidationAlert(false);
    } else {
      const cleanedErrors = {};
      Object.keys(allErrors).forEach(key => {
        if (!['price', 'taxClass', 'stock'].includes(key)) {
          cleanedErrors[key] = allErrors[key];
        }
      });
      
      setValidationErrors(cleanedErrors);
      setShowValidationAlert(true);
      
      // NEW: Auto-scroll to error
      setTimeout(scrollToError, 100);
      
      setTimeout(() => {
        setShowValidationAlert(false);
      }, 10000);
    }
  };

  const getCurrentProgress = () => {
    const completedSections = [];
    
    if (isTabValid(0)) completedSections.push(0);
    if (isTabValid(1)) completedSections.push(1);
    if (isTabValid(2)) completedSections.push(2);
    if (isTabValid(3)) completedSections.push(3);
    
    return (completedSections.length / tabs.length) * 100;
  };

  // UPDATED: Show tick only after Package tab is filled (optional)
  const isTabCompleted = (tabId) => {
    // For Package tab (tab 3), only show tick if at least one package is filled
    if (tabId === 3) {
      const packages = formData.packages || [];
      return packages.some(pkg => {
        return pkg.productName || 
               pkg.weight || 
               pkg.length || 
               pkg.width || 
               pkg.height ||
               pkg.coverImage ||
               pkg.image1 ||
               pkg.image2 ||
               pkg.image3;
      });
    }
    // For other tabs, show tick if valid
    return isTabValid(tabId);
  };

  const handleNext = () => {
    const errors = validateTab(activeTab);
    if (Object.keys(errors).length === 0) {
      setActiveTab(prev => Math.min(prev + 1, tabs.length - 1));
      setValidationErrors({});
      setShowValidationAlert(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setValidationErrors(errors);
      setShowValidationAlert(true);
      
      // NEW: Auto-scroll to error
      setTimeout(scrollToError, 100);
    }
  };

  const handlePrev = () => {
    setActiveTab(prev => Math.max(prev - 1, 0));
    setValidationErrors({});
    setShowValidationAlert(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderActiveTabComponent = () => {
    const ActiveComponent = tabs[activeTab].component;
    
    const commonProps = {
      formData,
      updateFormData,
      servicesApi,
      api,
      validationErrors,
      onNext: handleNext,
      onPrev: handlePrev,
      setError: (field, message) => {
        setValidationErrors(prev => ({
          ...prev,
          [field]: message
        }));
      }
    };

    if (activeTab === tabs.length - 1) {
      return (
        <ActiveComponent 
          {...commonProps} 
          onPreview={() => setShowPreview(true)}
          onPublish={handleSavePublish}
          isPublishDisabled={getCurrentProgress() < 100}
        />
      );
    }

    return <ActiveComponent {...commonProps} />;
  };

  const prepareProductData = (status) => {
  let packagesArray = []; 
  
  if (formData.packages?.length > 0) {
    packagesArray = formData.packages
      .filter(pkg => {
        // Check if package has ANY meaningful data
        const hasData = (pkg.productName && pkg.productName.trim()) || 
                        (pkg.weight && pkg.weight.toString().trim()) || 
                        (pkg.length && pkg.length.toString().trim()) || 
                        (pkg.width && pkg.width.toString().trim()) || 
                        (pkg.height && pkg.height.toString().trim()) ||
                        pkg.coverImage ||
                        pkg.image1 ||
                        pkg.image2 ||
                        pkg.image3 ||
                        (pkg.images?.length > 0);
        
        return hasData;
      })
      .map(pkg => {
        // Convert all numeric fields to strings and trim
        const weight = pkg.weight ? pkg.weight.toString().trim() : '';
        let length = pkg.length ? pkg.length.toString().trim() : '';
        let width = pkg.width ? pkg.width.toString().trim() : '';
        let height = pkg.height ? pkg.height.toString().trim() : '';
        
        // CRITICAL: Convert feet to cm before saving (admin panel only uses cm)
        if (pkg.dimensionUnit === 'ft' && (length || width || height)) {
          const ftToCm = 30.48; // 1 foot = 30.48 cm
          
          if (length) {
            length = (parseFloat(length) * ftToCm).toFixed(2);
          }
          if (width) {
            width = (parseFloat(width) * ftToCm).toFixed(2);
          }
          if (height) {
            height = (parseFloat(height) * ftToCm).toFixed(2);
          }
        }
        
        const mapped = {
          productName: pkg.productName || '',
          weight: weight,
          weightUnit: weight ? (pkg.weightUnit || 'kg') : '',
          length: length,
          width: width,
          height: height,
          dimensionUnit: 'cm', // ALWAYS save as cm (convert from ft if needed)
          coverImage: pkg.coverImage || '',
          image1: pkg.image1 || '',
          image2: pkg.image2 || '',
          image3: pkg.image3 || '',
          images: pkg.images || [],
          imageUrl: pkg.imageUrl || pkg.coverImage || ''
        };
        
        return mapped;
      });
  }
  
  // Fallback: create package from individual weight/dimension fields
  if (packagesArray.length === 0 && (formData.weight || formData.length || formData.width || formData.height)) {
    const weight = formData.weight ? formData.weight.toString().trim() : '';
    let length = formData.length ? formData.length.toString().trim() : '';
    let width = formData.width ? formData.width.toString().trim() : '';
    let height = formData.height ? formData.height.toString().trim() : '';
    
    // Convert feet to cm if needed
    if (formData.dimensionUnit === 'ft' && (length || width || height)) {
      const ftToCm = 30.48;
      if (length) length = (parseFloat(length) * ftToCm).toFixed(2);
      if (width) width = (parseFloat(width) * ftToCm).toFixed(2);
      if (height) height = (parseFloat(height) * ftToCm).toFixed(2);
    }
    
    packagesArray = [{
      productName: '',
      weight: weight,
      weightUnit: weight ? (formData.weightUnit || 'kg') : '',
      length: length,
      width: width,
      height: height,
      dimensionUnit: 'cm', // ALWAYS save as cm
      coverImage: '',
      image1: '',
      image2: '',
      image3: '',
      images: [],
      imageUrl: ''
    }];
  }

  const productData = {
    productName: formData.productName,
    description: formData.fullDescription,
    shortDescription: formData.shortDescription,
    price: formData.transferPrice,
    taxClass: formData.gst,
    HSN: formData.hsnCode,
    manufacturer: formData.brandid,
    
    manufacturerName: formData.manufacturerName,
    manufacturerAddress: formData.manufacturerAddress,
    manufacturerPincode: formData.manufacturerPincode,
    
    packerName: formData.packerName,
    packerAddress: formData.packerAddress,
    packerPincode: formData.packerPincode,
    sameAsManufacturer: formData.sameAsManufacturer,
    
    importerName: formData.importerName,
    importerAddress: formData.importerAddress,
    importerPincode: formData.importerPincode,
    
    categories: formData.categories,
    originCountry: formData.countryOfOrigin,
    stock: Number(formData.stockCount) || 0,
    images: formData.images || [],
    videos: formData.videoFile ? [formData.videoFile] : (formData.videoUrl ? [formData.videoUrl] : []),
    metaInformation: {
      keywords: formData.keywords || []
    },
    specifications: {
      packages: packagesArray,
      weight: packagesArray[0]?.weight || '',
      weightUnit: packagesArray[0]?.weightUnit || '',
      dimensions: {
        length: packagesArray[0]?.length || '',
        width: packagesArray[0]?.width || '',
        height: packagesArray[0]?.height || '',
        unit: 'cm' // ALWAYS cm in database
      },
      additionalSpecs: formData.additionalSpecs || []
    },
    status: status
  };

  if (match?.params?.id) {
    productData._id = match.params.id;
  }
  
  return productData;
};

  const handleSaveDraft = async () => {
    try {
      if (!formData.coverImage) {
        setValidationErrors({ coverImage: 'Cover image is required even for draft' });
        setShowValidationAlert(true);
        setActiveTab(1);
        setTimeout(scrollToError, 100);
        return;
      }

      const productData = prepareProductData('draft');
      const response = await api.product.addNewProduct(productData);
      
      if (response) {
        alert('Product saved as draft successfully!');
        window.location.assign("/my-inventory/new_product_request/manual");
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    }
  };

  const handleSavePublish = async () => {
    let allErrors = {};
    let hasErrors = false;
    
    for (let i = 0; i < tabs.length; i++) {
      const tabErrors = validateTab(i);
      if (Object.keys(tabErrors).length > 0) {
        allErrors = { ...allErrors, ...tabErrors };
        hasErrors = true;
      }
    }
    
    if (hasErrors) {
      setValidationErrors(allErrors);
      setShowValidationAlert(true);
      
      // NEW: Auto-scroll to error and switch to first error tab
      for (let i = 0; i < tabs.length; i++) {
        if (Object.keys(validateTab(i)).length > 0) {
          setActiveTab(i);
          setTimeout(scrollToError, 100);
          break;
        }
      }
      return;
    }

    try {
      const productData = prepareProductData('pending');
      const response = await api.product.addNewProduct(productData);
      
      if (response) {
        alert(match?.params?.id ? 'Product updated successfully!' : 'Product published successfully!');
        window.location.assign("/my-inventory/new_product_request/manual");
      }
    } catch (error) {
      console.error('Error publishing product:', error);
      alert('Failed to publish product. Please try again.');
    }
  };

  if (showPreview) {
    return (
      <ProductPreview 
        formData={formData} 
        onBack={() => setShowPreview(false)}
        onPublish={handleSavePublish}
      />
    );
  }

  if (isLoading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading product data...</p>
        </div>
      </Container>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Container fluid className="p-0">
        <Row className="g-0">
          <Col md={3} lg={3} className="bg-white border-end" style={{ 
            minHeight: '100vh',
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: '100vh',
            overflowY: 'auto'
          }}>
            <div className="p-3">
              <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                {match?.params?.id ? 'Edit Product' : 'Add New Product'}
              </h4>
              <p className="text-muted small mb-4">
                Create and publish your product to start selling
              </p>

              <div className="d-flex flex-column gap-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className="btn text-start d-flex align-items-center p-2 border-0 tab-button"
                    style={{
                      backgroundColor: activeTab === tab.id ? '#d4edda' : 'transparent',
                      color: activeTab === tab.id ? '#155724' : '#6c757d',
                      borderLeft: activeTab === tab.id ? '3px solid #28a745' : '3px solid transparent',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                      fontWeight: activeTab === tab.id ? '600' : '400',
                      fontSize: '0.9rem',
                      outline: 'none',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(40, 167, 69, 0.25)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <i className={`${tab.icon} me-3`} style={{ fontSize: '1.1rem' }}></i>
                    <span className="flex-grow-1">
                      {tab.title}
                      {tab.optional && <span className="badge bg-secondary ms-2" style={{ fontSize: '0.65rem' }}>Optional</span>}
                    </span>
                    {isTabCompleted(tab.id) && (
                      <i className="bi bi-check-circle-fill text-success"></i>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </Col>

          <Col md={9} lg={9} style={{ position: 'relative', backgroundColor: '#f8f9fa' }}>
            <div style={{
              position: 'sticky',
              top: 0,
              background: 'linear-gradient(135deg, #165b5b 0%, #165b5b 100%)',
              color: 'white',
              padding: '1rem 1.5rem',
              boxShadow: '0 2px 8px rgba(122, 243, 142, 0.15)',
              zIndex: 1020,
              marginBottom: 0
            }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
                  {tabs[activeTab].title}
                </h4>
                <span 
                  className="badge" 
                  style={{ 
                    backgroundColor: 'white',
                    color: '#165b5b',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px'
                  }}
                >
                  Step {activeTab + 1} of {tabs.length}
                </span>
              </div>
              <ProgressBar 
                now={((activeTab + 1) / tabs.length) * 100} 
                style={{ 
                  height: '6px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width:'800px'
                }}
              />
            </div>

            <div style={{ 
              padding: '1.5rem',
              backgroundColor: '#f8f9fa',
              minHeight: 'calc(100vh - 100px)',
              position: 'relative'
            }} ref={formContentRef}>
              {showValidationAlert && Object.keys(validationErrors).length > 0 && (
                <div 
                  ref={alertRef}
                  style={{
                    position: 'sticky',
                    top: '100px',
                    zIndex: 1010,
                    marginBottom: '1.5rem'
                  }}
                >
                  <Alert 
                    variant="danger" 
                    dismissible 
                    onClose={() => setShowValidationAlert(false)}
                    style={{
                      boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)',
                      animation: 'slideDown 0.3s ease'
                    }}
                  >
                    <Alert.Heading style={{ fontSize: '1rem', fontWeight: '600' }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Please fix the following errors:
                    </Alert.Heading>
                    <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                      {Object.entries(validationErrors).map(([field, error]) => {
                        const fieldLabels = {
                          productName: 'Product Name',
                          countryOfOrigin: 'Country of Origin',
                          brandid: 'Brand',
                          brand: 'Brand',
                          grandCategory: 'Grand Category',
                          parentCategory: 'Parent Category',
                          childCategory: 'Child Category',
                          grandChildCategory: 'Grand Child Category',
                          keywords: 'Keywords',
                          shortDescription: 'Short Description',
                          fullDescription: 'Full Description',
                          coverImage: 'Cover Image',
                          images: 'Product Images',
                          transferPrice: 'Transfer Price',
                          gst: 'GST',
                          hsnCode: 'HSN Code',
                          stockCount: 'Stock Count',
                          packageWeight: 'Package Weight',
                          packageLength: 'Package Length',
                          packageWidth: 'Package Width',
                          packageHeight: 'Package Height'
                        };
                        
                        const displayName = fieldLabels[field] || field.replace(/([A-Z])/g, ' $1').trim();
                        
                        return (
                          <li key={field}>
                            <strong>{displayName}:</strong> {error}
                          </li>
                        );
                      })}
                    </ul>
                  </Alert>
                </div>
              )}

              <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="p-3">
                  {renderActiveTabComponent()}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .progress-bar {
          background-color: white !important;
          border-radius: 10px !important;
        }
        
        .card {
          border-radius: 8px !important;
        }
        
        .btn:hover {
          transform: translateY(-1px);
          transition: all 0.2s ease;
        }
        
        .btn:active {
          transform: translateY(0);
        }

        .tab-button:hover {
          transform: translateX(2px);
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .position-sticky {
          position: -webkit-sticky;
          position: sticky;
          will-change: transform;
        }
        
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};